import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { useModalScrollLock } from '../hooks/useModalScrollLock'
import {
  ArrowLeft,
  User,
  MapPin,
  CreditCard,
  Truck,
  Package,
  AlertCircle,
  ExternalLink,
  Download,
  FileText,
  RefreshCw,
  XCircle,
  Calendar,
  Search,
} from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import LoadingButton from '../components/LoadingButton'
import { useShipmentActions } from '../hooks/useShipmentActions'
import { formatEnumString } from '../utils/stringHelpers'

interface Order {
  id: string
  orderNumber: string
  status: string
  paymentStatus: string
  shippingStatus?: string
  pickupStatus?: string
  totalAmount: number
  discountAmount: number
  taxAmount: number
  shippingCost: number
  paymentMethod: string
  transactionId?: string
  razorpayOrderId?: string
  razorpayPaymentId?: string
  shiprocketOrderId?: string
  shiprocketShipmentId?: string
  awbCode?: string
  courierName?: string
  trackingUrl?: string
  labelUrl?: string
  invoiceUrl?: string
  trackingNumber?: string
  estimatedDelivery?: string
  notes?: string
  createdAt: string
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
  items: Array<{
    id: string
    productId: string
    product: {
      id: string
      name: string
      sku?: string
      price: number
    }
    quantity: number
    price: number
    subtotal: number
  }>
  shippingAddress: any
  billingAddress: any
  timeline: Array<{
    id: string
    status: string
    note: string
    timestamp: string
  }>
  trackingHistory?: Array<{
    id: string
    status: string
    location?: string
    activity?: string
    timestamp: string
  }>
}

const ORDER_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { value: 'PROCESSING', label: 'Processing', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'SHIPPED', label: 'Shipped', color: 'bg-purple-100 text-purple-800' },
  { value: 'DELIVERED', label: 'Delivered', color: 'bg-green-100 text-green-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
]

const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'PAID', label: 'Paid', color: 'bg-green-100 text-green-800' },
  { value: 'FAILED', label: 'Failed', color: 'bg-red-100 text-red-800' },
  { value: 'REFUNDED', label: 'Refunded', color: 'bg-gray-100 text-gray-800' },
]

const renderOrderStatusBadge = (statusStr?: string) => {
  if (!statusStr) return null
  const statusUpper = statusStr.trim().toUpperCase()
  let label = formatEnumString(statusStr)
  let colorClass = 'bg-gray-100 text-gray-800 border border-gray-200'

  switch (statusUpper) {
    case 'PENDING':
      label = 'Pending'
      colorClass = 'bg-yellow-100 text-yellow-800 border border-yellow-200'
      break
    case 'CONFIRMED':
      label = 'Confirmed'
      colorClass = 'bg-blue-100 text-blue-800 border border-blue-200'
      break
    case 'PROCESSING':
      label = 'Processing'
      colorClass = 'bg-indigo-100 text-indigo-800 border border-indigo-200'
      break
    case 'SHIPPED':
      label = 'Shipped'
      colorClass = 'bg-purple-100 text-purple-800 border border-purple-200'
      break
    case 'OUT_FOR_DELIVERY':
      label = 'Out For Delivery'
      colorClass = 'bg-amber-100 text-amber-800 border border-amber-200'
      break
    case 'DELIVERED':
      label = 'Delivered'
      colorClass = 'bg-emerald-100 text-emerald-800 border border-emerald-200'
      break
    case 'CANCELLED':
      label = 'Cancelled'
      colorClass = 'bg-red-100 text-red-800 border border-red-200'
      break
    case 'RETURNED':
      label = 'Returned'
      colorClass = 'bg-rose-100 text-rose-800 border border-rose-200'
      break
    case 'REFUNDED':
      label = 'Refunded'
      colorClass = 'bg-gray-100 text-gray-800 border border-gray-200'
      break
    default:
      label = formatEnumString(statusStr)
      break
  }

  return (
    <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full inline-block shadow-2xs ${colorClass}`}>
      {label}
    </span>
  )
}

const renderPickupBadge = (statusStr?: string) => {
  if (!statusStr || statusStr === 'NOT_SCHEDULED') {
    return (
      <span className="inline-block px-2.5 py-0.5 mt-0.5 bg-gray-100 text-gray-700 border border-gray-200 text-xs font-bold rounded-full">
        Not Scheduled
      </span>
    )
  }
  const formattedText = formatEnumString(statusStr)

  let badgeColor = 'bg-blue-100 text-blue-800 border border-blue-200'
  if (statusStr.includes('COMPLETED') || statusStr.includes('DELIVERED')) {
    badgeColor = 'bg-green-100 text-green-800 border border-green-200'
  } else if (statusStr.includes('PENDING') || statusStr.includes('SCHEDULED') || statusStr.includes('ASSIGNED')) {
    badgeColor = 'bg-yellow-100 text-yellow-800 border border-yellow-200'
  } else if (statusStr.includes('CANCELLED') || statusStr.includes('FAILED')) {
    badgeColor = 'bg-red-100 text-red-800 border border-red-200'
  }

  return (
    <span className={`inline-block px-2.5 py-0.5 mt-0.5 text-xs font-bold rounded-full ${badgeColor}`}>
      {formattedText}
    </span>
  )
}

export default function OrderDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [refundButtonState, setRefundButtonState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Shiprocket button action loading states
  const [createShipmentState, setCreateShipmentState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [generateAwbState, setGenerateAwbState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [schedulePickupState, setSchedulePickupState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [cancelShipmentState, setCancelShipmentState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [fullFulfillState, setFullFulfillState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [labelState, setLabelState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [invoiceState, setInvoiceState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  useModalScrollLock(showRefundModal)

  useEffect(() => {
    fetchOrder()
  }, [id])

  const fetchOrder = async () => {
    if (!id) return
    try {
      setLoading(true)
      const response = await api.getOrder(id)
      setOrder(response.data.data)
    } catch (error) {
      toast.error('Failed to load order details')
    } finally {
      setLoading(false)
    }
  }

  const shipmentActions = useShipmentActions(order)

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return
    try {
      setUpdating(true)
      await api.updateOrderStatus(order.id, newStatus)
      toast.success('Order status updated')
      fetchOrder()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update order status')
      fetchOrder()
    } finally {
      setUpdating(false)
    }
  }

  const handlePaymentStatusChange = async (newStatus: string) => {
    if (!order) return
    try {
      setUpdating(true)
      await api.updatePaymentStatus(order.id, newStatus)
      toast.success('Payment status updated')
      fetchOrder()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update payment status')
      fetchOrder()
    } finally {
      setUpdating(false)
    }
  }

  // Action 1: Create Shipment
  const handleCreateShipment = async () => {
    if (!order || !shipmentActions.canCreateShipment.allowed) return
    try {
      setCreateShipmentState('loading')
      await api.createShipment(order.id)
      setCreateShipmentState('success')
      toast.success('Shipment created in Shiprocket')
      setTimeout(() => {
        fetchOrder()
        setCreateShipmentState('idle')
      }, 800)
    } catch (error: any) {
      setCreateShipmentState('error')
      toast.error(error.response?.data?.message || 'Failed to create shipment')
      fetchOrder()
      setTimeout(() => setCreateShipmentState('idle'), 2000)
    }
  }

  // Action 2 & 3: Generate AWB / Assign Courier
  const handleGenerateAwb = async () => {
    if (!order || !shipmentActions.canAssignAwb.allowed) return
    try {
      setGenerateAwbState('loading')
      await api.generateAwb(order.id)
      setGenerateAwbState('success')
      toast.success('AWB & Courier assigned')
      setTimeout(() => {
        fetchOrder()
        setGenerateAwbState('idle')
      }, 800)
    } catch (error: any) {
      setGenerateAwbState('error')
      toast.error(error.response?.data?.message || 'Failed to generate AWB')
      fetchOrder()
      setTimeout(() => setGenerateAwbState('idle'), 2000)
    }
  }

  // Action 4: Schedule Pickup
  const handleSchedulePickup = async () => {
    if (!order || !shipmentActions.canSchedulePickup.allowed) return
    try {
      setSchedulePickupState('loading')
      await api.schedulePickup(order.id)
      setSchedulePickupState('success')
      toast.success('Pickup scheduled successfully')
      setTimeout(() => {
        fetchOrder()
        setSchedulePickupState('idle')
      }, 800)
    } catch (error: any) {
      setSchedulePickupState('error')
      toast.error(error.response?.data?.message || 'Failed to schedule pickup')
      fetchOrder()
      setTimeout(() => setSchedulePickupState('idle'), 2000)
    }
  }

  // Action 5: Cancel Shipment
  const handleCancelShipment = async () => {
    if (!order || !shipmentActions.canCancelShipment.allowed) return
    try {
      setCancelShipmentState('loading')
      await api.cancelShipment(order.id)
      setCancelShipmentState('success')
      toast.success('Shipment cancelled in Shiprocket')
      setTimeout(() => {
        fetchOrder()
        setCancelShipmentState('idle')
      }, 800)
    } catch (error: any) {
      setCancelShipmentState('error')
      toast.error(error.response?.data?.message || 'Failed to cancel shipment')
      fetchOrder()
      setTimeout(() => setCancelShipmentState('idle'), 2000)
    }
  }

  // Action 6: Track Shipment
  const handleTrackShipment = () => {
    if (!order || !shipmentActions.canTrackShipment.allowed) return
    const url = order.trackingUrl || `https://shiprocket.co/tracking/${order.awbCode || ''}`
    window.open(url, '_blank')
  }

  // Action 7: Download Shipping Label
  const handleDownloadLabel = async () => {
    if (!order || !shipmentActions.canDownloadLabel.allowed) return
    try {
      setLabelState('loading')
      const res = await api.getLabel(order.id)
      setLabelState('success')
      const url = res.data?.data?.labelUrl || order.labelUrl
      if (url) {
        window.open(url, '_blank')
      } else {
        toast.error('Label URL unavailable')
      }
      setTimeout(() => setLabelState('idle'), 1000)
    } catch (error: any) {
      setLabelState('error')
      toast.error(error.response?.data?.message || 'Failed to fetch label')
      fetchOrder()
      setTimeout(() => setLabelState('idle'), 2000)
    }
  }

  // Action 8: Download Invoice
  const handleDownloadInvoice = async () => {
    if (!order || !shipmentActions.canDownloadInvoice.allowed) return
    try {
      setInvoiceState('loading')
      const res = await api.getInvoice(order.id)
      setInvoiceState('success')
      const url = res.data?.data?.invoiceUrl || order.invoiceUrl
      if (url) {
        window.open(url, '_blank')
      } else {
        toast.error('Invoice URL unavailable')
      }
      setTimeout(() => setInvoiceState('idle'), 1000)
    } catch (error: any) {
      setInvoiceState('error')
      toast.error(error.response?.data?.message || 'Failed to fetch invoice')
      fetchOrder()
      setTimeout(() => setInvoiceState('idle'), 2000)
    }
  }

  // Action 9: Run Full Pipeline
  const handleFullFulfillment = async () => {
    if (!order || !shipmentActions.canFullFulfill.allowed) return
    try {
      setFullFulfillState('loading')
      await api.retryFulfillment(order.id)
      setFullFulfillState('success')
      toast.success('Full Shiprocket pipeline executed!')
      setTimeout(() => {
        fetchOrder()
        setFullFulfillState('idle')
      }, 800)
    } catch (error: any) {
      setFullFulfillState('error')
      toast.error(error.response?.data?.message || 'Fulfillment pipeline error')
      fetchOrder()
      setTimeout(() => setFullFulfillState('idle'), 2000)
    }
  }

  const handleRefund = async () => {
    if (!order) return
    const statusUpper = (order.status || '').toUpperCase()
    if (statusUpper === 'DELIVERED') {
      toast.error('Refunds are not allowed for delivered orders')
      return
    }
    const paymentId = order.razorpayPaymentId || order.transactionId
    if (!paymentId) {
      toast.error('No payment ID found. Cannot process refund.')
      return
    }
    if (order.paymentStatus !== 'PAID') {
      toast.error('Only paid orders can be refunded')
      return
    }
    setShowRefundModal(true)
  }

  const handleConfirmRefund = async () => {
    if (!order) return
    const paymentId = order.razorpayPaymentId || order.transactionId
    if (!paymentId) return
    try {
      setRefundButtonState('loading')
      await api.refundPayment(paymentId, order.totalAmount)
      setRefundButtonState('success')
      toast.success('Refund processed successfully')
      setShowRefundModal(false)
      setTimeout(() => {
        fetchOrder()
        setRefundButtonState('idle')
      }, 500)
    } catch (error: any) {
      setRefundButtonState('error')
      toast.error(error.response?.data?.message || 'Failed to process refund')
      setTimeout(() => setRefundButtonState('idle'), 2000)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
        <p className="text-gray-600 text-lg mb-4">Order not found</p>
        <button
          onClick={() => navigate('/orders')}
          className="flex items-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] mx-auto font-medium"
        >
          <ArrowLeft size={16} />
          Back to Orders
        </button>
      </div>
    )
  }

  const isDelivered = (order.status || '').toUpperCase() === 'DELIVERED'
  const isRefundDisabled = isDelivered || order.paymentStatus === 'REFUNDED' || order.paymentStatus !== 'PAID' || !order.razorpayPaymentId

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="flex items-center gap-2 text-[#6D3A0D] hover:bg-[#F6EEE7] px-3 py-2 rounded-lg transition"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">{order.orderNumber}</h1>
              {renderOrderStatusBadge(order.status)}
            </div>
            <p className="text-gray-600 text-xs sm:text-sm mt-1">
              Placed on {new Date(order.createdAt).toLocaleDateString()} at {new Date(order.createdAt).toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LoadingButton
            type="button"
            onClick={handleRefund}
            state={isRefundDisabled ? 'disabled' : refundButtonState}
            disabled={isRefundDisabled}
            title={
              isDelivered
                ? 'Refunds are not allowed for delivered orders'
                : order.paymentStatus === 'REFUNDED'
                  ? 'Order is already refunded'
                  : order.paymentStatus !== 'PAID'
                    ? 'Only paid orders can be refunded'
                    : !order.razorpayPaymentId
                      ? 'No online payment transaction found for this order'
                      : 'Refund Order'
            }
            variant="danger"
            size="md"
            className="px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Refund Order
          </LoadingButton>
        </div>
      </div>

      {/* 🚢 SHIPROCKET MANAGEMENT CARD */}
      <div className="bg-white rounded-xl border-2 border-indigo-100 shadow-sm p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-indigo-50 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl">
              <Truck size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Shiprocket Logistics Management</h2>
              <p className="text-xs text-gray-500">Live shipping actions, courier dispatching and tracking</p>
            </div>
          </div>
          <LoadingButton
            type="button"
            onClick={handleFullFulfillment}
            state={fullFulfillState}
            variant="primary"
            size="sm"
            disabled={!shipmentActions.canFullFulfill.allowed}
            title={shipmentActions.canFullFulfill.reason}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs px-4 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} /> Run Full Auto-Fulfillment
          </LoadingButton>
        </div>

        {/* Shipment Info Matrix */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/60">
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">Shipment ID</p>
            <p className="text-sm font-bold text-gray-800">{order.shiprocketShipmentId || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">AWB Number</p>
            <p className="text-sm font-mono font-bold text-indigo-700">{order.awbCode || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">Courier Partner</p>
            <p className="text-sm font-medium text-gray-800">{order.courierName || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">Shipping Status</p>
            <span className="inline-block px-2.5 py-0.5 mt-0.5 bg-indigo-100 text-indigo-800 text-xs font-bold rounded-full">
              {formatEnumString(order.shippingStatus || 'PENDING')}
            </span>
          </div>
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">Pickup Status</p>
            {renderPickupBadge(order.pickupStatus)}
          </div>
          <div>
            <p className="text-xs font-semibold text-indigo-900 uppercase">Tracking Link</p>
            {order.awbCode && (order.shippingStatus || '').toUpperCase() !== 'CANCELLED' ? (
              <a
                href={order.trackingUrl || `https://shiprocket.co/tracking/${order.awbCode}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 font-semibold hover:underline mt-1"
              >
                Track Live <ExternalLink size={12} />
              </a>
            ) : (
              <p className="text-xs text-gray-400 font-medium">No Active AWB</p>
            )}
          </div>
        </div>

        {/* 8 Step Action Buttons Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
          {/* Button 1: Create Shipment */}
          <LoadingButton
            type="button"
            onClick={handleCreateShipment}
            state={createShipmentState}
            variant="secondary"
            disabled={!shipmentActions.canCreateShipment.allowed}
            title={shipmentActions.canCreateShipment.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Package size={15} /> 1. Create Shipment
          </LoadingButton>

          {/* Button 2 & 3: Assign Courier & AWB */}
          <LoadingButton
            type="button"
            onClick={handleGenerateAwb}
            state={generateAwbState}
            variant="secondary"
            disabled={!shipmentActions.canAssignAwb.allowed}
            title={shipmentActions.canAssignAwb.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Truck size={15} /> 2. Assign AWB/Courier
          </LoadingButton>

          {/* Button 4: Schedule Pickup */}
          <LoadingButton
            type="button"
            onClick={handleSchedulePickup}
            state={schedulePickupState}
            variant="secondary"
            disabled={!shipmentActions.canSchedulePickup.allowed}
            title={shipmentActions.canSchedulePickup.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calendar size={15} /> 3. Schedule Pickup
          </LoadingButton>

          {/* Button 5: Cancel Shipment */}
          <LoadingButton
            type="button"
            onClick={handleCancelShipment}
            state={cancelShipmentState}
            variant="danger"
            disabled={!shipmentActions.canCancelShipment.allowed}
            title={shipmentActions.canCancelShipment.reason}
            className="w-full text-xs font-semibold py-2.5 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle size={15} /> 4. Cancel Shipment
          </LoadingButton>

          {/* Button 6: Track Shipment */}
          <button
            type="button"
            onClick={handleTrackShipment}
            disabled={!shipmentActions.canTrackShipment.allowed}
            title={shipmentActions.canTrackShipment.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search size={15} /> 5. Track Shipment
          </button>

          {/* Button 7: Download Label */}
          <LoadingButton
            type="button"
            onClick={handleDownloadLabel}
            state={labelState}
            variant="secondary"
            disabled={!shipmentActions.canDownloadLabel.allowed}
            title={shipmentActions.canDownloadLabel.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={15} /> 6. Download Label
          </LoadingButton>

          <LoadingButton
            type="button"
            onClick={handleDownloadInvoice}
            state={invoiceState}
            variant="secondary"
            disabled={!shipmentActions.canDownloadInvoice.allowed}
            title={shipmentActions.canDownloadInvoice.reason}
            className="w-full text-xs font-semibold py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText size={15} /> 7. Download Invoice
          </LoadingButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Left Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Status */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 space-y-4">
            <h2 className="text-xl font-bold text-[#6D3A0D]">Order Status Controls</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#6D3A0D] mb-2">Order Status</label>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating || order.status === 'CANCELLED'}
                  title={order.status === 'CANCELLED' ? 'Cancelled orders cannot be changed' : 'Change Order Status'}
                  className="w-full px-4 py-2 border-2 border-[#F6EEE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ORDER_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#6D3A0D] mb-2">Payment Status</label>
                <select
                  value={order.paymentStatus}
                  onChange={(e) => handlePaymentStatusChange(e.target.value)}
                  disabled={updating}
                  className="w-full px-4 py-2 border-2 border-[#F6EEE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] bg-white text-sm disabled:opacity-50"
                >
                  {PAYMENT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Ordered Products */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-4">Ordered Products</h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between items-center pb-4 border-b border-[#F6EEE7]">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{item.product.name}</h4>
                    {item.product.sku && <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>}
                    <p className="text-xs text-gray-600">₹{item.price} × {item.quantity}</p>
                  </div>
                  <p className="font-bold text-[#6D3A0D]">₹{item.subtotal.toFixed(2)}</p>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            {(() => {
              const itemsSubtotal = order.items.reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0);
              const discount = Number(order.discountAmount) || 0;
              const tax = Number(order.taxAmount) || 0;
              const shipping = Number(order.shippingCost) || 0;
              const total = Number(order.totalAmount) || 0;

              return (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Items Subtotal:</span>
                    <span className="font-medium text-gray-900">₹{itemsSubtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Coupon Discount:</span>
                      <span className="font-medium">-₹{discount.toFixed(2)}</span>
                    </div>
                  )}
                  {tax > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Tax / GST:</span>
                      <span className="font-medium text-gray-900">₹{tax.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping Charges:</span>
                    <span className="font-medium text-gray-900">
                      {shipping > 0 ? `₹${shipping.toFixed(2)}` : 'Free'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex justify-between font-bold text-base text-gray-900">
                    <span>Grand Total:</span>
                    <span className="text-[#6D3A0D]">₹{total.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Tracking & Logistics History */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-4">Logistics Tracking History</h2>
            <div className="space-y-3">
              {order.trackingHistory && order.trackingHistory.length > 0 ? (
                order.trackingHistory.map((item) => (
                  <div key={item.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs flex justify-between items-start">
                    <div>
                      <span className="font-bold text-indigo-800">{formatEnumString(item.status)}</span>
                      {item.activity && <p className="text-gray-700 mt-0.5">{item.activity}</p>}
                    </div>
                    <span className="text-gray-400 flex-shrink-0">{new Date(item.timestamp).toLocaleString()}</span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500 text-center py-4">No tracking history recorded yet</p>
              )}
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-4">Order Timeline</h2>
            <div className="space-y-4">
              {order.timeline && order.timeline.length > 0 ? (
                order.timeline.map((event, index) => (
                  <div key={event.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-3.5 h-3.5 rounded-full ${event.status === 'DELIVERED' || event.status === 'CONFIRMED' ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                      {index < order.timeline.length - 1 && <div className="w-0.5 h-10 bg-gray-200"></div>}
                    </div>
                    <div className="pb-2">
                      <p className="font-semibold text-gray-900 text-sm">{formatEnumString(event.status)}</p>
                      {event.note && <p className="text-xs text-gray-600">{event.note}</p>}
                      <p className="text-xs text-gray-400">{new Date(event.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 text-center py-4 text-xs">No timeline events</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar Details */}
        <div className="space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h3 className="font-bold text-[#6D3A0D] mb-4 flex items-center gap-2 text-base">
              <User size={18} />
              Customer Details
            </h3>
            <div className="space-y-3 text-xs">
              <div>
                <p className="text-gray-500 font-semibold uppercase">Name</p>
                <p className="font-bold text-gray-900">{order.user.firstName} {order.user.lastName}</p>
              </div>
              <div>
                <p className="text-gray-500 font-semibold uppercase">Email</p>
                <p className="font-bold text-gray-900">{order.user.email}</p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h3 className="font-bold text-[#6D3A0D] mb-4 flex items-center gap-2 text-base">
              <MapPin size={18} />
              Shipping Address
            </h3>
            {order.shippingAddress ? (
              <div className="text-xs space-y-1 text-gray-700">
                <p className="font-bold text-gray-900">{order.shippingAddress.fullName}</p>
                <p>{order.shippingAddress.street}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
                <p>{order.shippingAddress.country || 'IN'}</p>
                <p className="font-semibold text-gray-800 pt-1">Phone: {order.shippingAddress.phone}</p>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No shipping address recorded</p>
            )}
          </div>
        </div>
      </div>

      {/* Refund Confirmation Modal */}
      {showRefundModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 z-[9999] overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl border-2 border-[#F6EEE7]">
            <h3 className="text-xl font-bold text-gray-900">Confirm Refund</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to refund ₹{order.totalAmount.toFixed(2)} to customer{' '}
              <span className="font-semibold">{order.user.email}</span>?
            </p>
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowRefundModal(false)}
                className="px-4 py-2 border-2 border-[#F6EEE7] text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
              >
                Cancel
              </button>
              <LoadingButton
                type="button"
                onClick={handleConfirmRefund}
                state={refundButtonState}
                variant="danger"
                size="sm"
                className="px-4 py-2 text-sm font-semibold"
                loadingText="Processing..."
                errorText="Failed"
              >
                Confirm Refund
              </LoadingButton>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
